import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { DialogContentText } from "@remo-co/ui-core/src/components/DialogContentText";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Popover } from "@remo-co/ui-core/src/components/Popover";
import { Table } from "@remo-co/ui-core/src/components/Table";
import { TableBody } from "@remo-co/ui-core/src/components/TableBody";
import { TableCell } from "@remo-co/ui-core/src/components/TableCell";
import { TableHead } from "@remo-co/ui-core/src/components/TableHead";
import { TableRow } from "@remo-co/ui-core/src/components/TableRow";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Check } from "@remo-co/ui-core/src/icons/Check";
import { Delete } from "@remo-co/ui-core/src/icons/Delete";
import { Info } from "@remo-co/ui-core/src/icons/Info";
import { ArrowDropDown } from "@remo-co/ui-core/src/icons/ArrowDropDown";
import "./ManagersList.scss";
import _ from "lodash";
import { selectMyCompany } from "modules/company/redux/selectors";
import { useI18n } from "i18n";
import logger from "logging/logger";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isTrialCompleted } from "modules/companyPlanSettings/utils/trial";
import { TRACKING_CONTEXT } from "modules/tracking";
import { IEvent } from "modules/event/types";
import { selectIsMobile } from "modules/deviceInfo";
import CheckboxList from "./CheckBoxList";
import AddCompanyMember from "./AddCompanyMember";
import UpgradePlanPopup from "../../upgradePlan/UpgradePlanPopup";
import useNotificationActions from "../../notification/hooks/useNotificationActions";
import {
  deleteMemberReq,
  getAllActiveEvents,
  getMembersReq,
  updateAssistantReq,
  updateMemberReq,
} from "./apis";

export interface IMember {
  email: string;
  id: string;
  pending: boolean;
  role: string;
  events?: string[];
}

interface MemberEditInfo {
  member?: IMember | null;
  role?: string;
  operation?: MemberOperation;
  events?: string[];
}

const ROLE_INFO: { [key: string]: string } = {
  owner: "Host",
  manager: "Event Manager",
  assistant: "Event Assistant",
};

enum MemberOperation {
  UPDATE = 1,
  DELETE = 0,
}

interface ManagersListProps {
  isEdit: boolean;
}

const ManagersList = ({ isEdit }: ManagersListProps): JSX.Element | null => {
  const company = useSelector(selectMyCompany);
  const isMobileView = useSelector(selectIsMobile);
  const { myCompanyPlan, maxAllowedManagers, maxAllowedAssistants } =
    useCompanyPlanSettings();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const [members, setMembers] = useState<IMember[]>([]);
  const [allEvents, setAllEvents] = useState<IEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditOption, setShowEditOption] = useState(false);
  const [showEventList, setShowEventList] = useState(false);
  const [showConfirmDialogue, setShowConfirmDialogue] = useState(false);
  const [selectedMember, setSelectedMember] = useState<IMember | null>(null);
  const [memberEditInfo, setMemberEditInfo] = useState<MemberEditInfo | null>(
    null,
  );
  const [anchorElm, setAnchorElm] = useState<HTMLButtonElement | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const { group } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n([
    "common",
    "company",
    "settings",
    "errorPage",
    "server",
  ]);
  const myTrialExpired = isTrialCompleted(myCompanyPlan);

  const getMembers = async (companyId: string) => {
    setIsLoading(true);
    const res = await getMembersReq(companyId);

    if (res?.isSuccess) {
      setMembers(res.members || []);
    } else {
      addErrorNotification({
        message: t("settings:list.manager.error"),
        position: "tc",
      });
    }

    setIsLoading(false);
  };

  const getAllEvents = async (companyId: string) => {
    if (!isEdit) {
      return;
    }
    setIsLoading(true);
    const res = await getAllActiveEvents(companyId);

    if (res?.isSuccess) {
      setAllEvents(res.events || []);
    } else {
      addErrorNotification({
        message: t("settings:fetch.events.list.error"),
        position: "tc",
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (company && company.id && !isLoading) {
      getMembers(company.id);
      getAllEvents(company.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(members) && company?.id) {
      group(company.id, {
        managers_count: members.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  const handleRemove = () => {
    if (selectedMember) {
      setShowConfirmDialogue(true);
      setMemberEditInfo({
        member: selectedMember,
        operation: MemberOperation.DELETE,
      });
    }
  };

  const handleMemberUpdate = (newRole: string) => {
    if (selectedMember?.role === newRole) {
      return;
    }

    if (selectedMember) {
      setShowConfirmDialogue(true);
      setShowEditOption(false);
      setMemberEditInfo({
        member: selectedMember,
        operation: MemberOperation.UPDATE,
        role: newRole,
      });
    }
  };

  const handleRoleClick = (anchor: HTMLButtonElement, member: IMember) => {
    if (isEdit) {
      setShowEditOption(true);
      setAnchorElm(anchor);
      setSelectedMember(member);
    }
  };

  const handleEventsClick = (anchor: HTMLButtonElement, member: IMember) => {
    if (isEdit && member.role === "assistant") {
      const events = member.events || [];

      setMemberEditInfo({
        member,
        events: [...events],
      });
      setAnchorElm(anchor);
      setShowEventList(true);
    }
  };

  const clearSelectedMember = () => {
    setSelectedMember(null);
    setMemberEditInfo(null);
    setShowConfirmDialogue(false);
    setShowEditOption(false);
    setAnchorElm(null);
  };

  const getPermissionText = (entity: IMember) => {
    if (entity?.role === "assistant") {
      return entity?.events?.length
        ? `${entity.events.length} Events`
        : t("settings:no.events");
    }

    return t("settings:all.events");
  };

  const getEventsPermission = (entity: IMember) => {
    if (entity.role !== "assistant") {
      return <div className="text">{t("settings:all.events")}</div>;
    }

    if (!allEvents || allEvents.length === 0) {
      return <div className="text">{t("settings:no.events")}</div>;
    }

    if (isEdit) {
      return (
        <Button
          variant="ghost"
          color="dark"
          size="md"
          onClick={(e) => handleEventsClick(e.currentTarget, entity)}
          endAdornment={<ArrowDropDown />}
        >
          {getPermissionText(entity)}
        </Button>
      );
    }

    return getPermissionText(entity);
  };

  const handleAddManager = () => {
    // If maximum members are added, then show upgrade popup
    // -1 is unlimited
    // reducing 1 for owner
    const maxAllowedMembers =
      maxAllowedManagers > -1
        ? maxAllowedManagers + maxAllowedAssistants
        : maxAllowedManagers;

    if (maxAllowedMembers > -1 && maxAllowedMembers <= members.length) {
      setUpgradeMessage(
        t("settings:members.max.limit.error", {
          limit: maxAllowedMembers,
        }),
      );
    } else {
      setIsAddOpen(true);
    }
  };

  const renderRows = () => {
    if (isLoading) {
      return t("loading");
    }

    return (
      <>
        {members.map((entity: IMember) => (
          <TableRow key={entity.id} data-testid={`row-${entity.email}`}>
            <TableCell>
              {entity.email} <i>{entity.pending ? "(pending)" : ""}</i>
            </TableCell>
            <TableCell>{getEventsPermission(entity)}</TableCell>
            <TableCell>
              {isEdit && entity.role !== "owner" ? (
                <Button
                  variant="ghost"
                  color="dark"
                  size="md"
                  onClick={(e) => handleRoleClick(e.currentTarget, entity)}
                  data-testid="context-menu"
                  endAdornment={<ArrowDropDown />}
                >
                  {ROLE_INFO[entity.role]}
                </Button>
              ) : (
                <div className="text">{ROLE_INFO[entity.role]}</div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const updateAssistantAccess = async (
    member: IMember,
    newEvents: string[] | undefined,
  ) => {
    if (!company) {
      addErrorNotification({
        message: t("common:error.unknown"),
        position: "tc",
      });

      return;
    }

    if (!member || member.role !== "assistant") {
      return;
    }

    const res = await updateAssistantReq(company.id, member.id, {
      pending: !!member.pending,
      events: newEvents,
    });

    if (res.isSuccess) {
      addSuccessNotification({
        message: t("settings:member.update.success", { email: member.email }),
        position: "tc",
      });
      setMembers(res.members);
    } else {
      logger.error(`[ManagersList][updateAssistantAccess] ${res.error}`);
      addErrorNotification({
        message: t("settings:member.update.error"),
        position: "tc",
      });
    }
    clearSelectedMember();
  };

  const handleUpdateMember = async () => {
    if (!company) {
      addErrorNotification({
        message: t("common:error.unknown"),
        position: "tc",
      });

      return;
    }

    const res = await updateMemberReq(
      company.id,
      selectedMember?.id as string,
      {
        pending: !!selectedMember?.pending,
        role: memberEditInfo?.role,
      },
    );

    if (res.isSuccess) {
      addSuccessNotification({
        message: t("settings:member.update.success", {
          email: selectedMember?.email,
        }),
        position: "tc",
      });
      setMembers(res.members);
    } else {
      logger.error(`[ManagersList][handleUpdateMember] ${res.error}`);
      addErrorNotification({
        message: t(res?.message || "settings:member.update.error", {
          maxAllowedManagers,
          maxAllowedAssistants,
        }),
        position: "tc",
      });
    }
    clearSelectedMember();
  };

  const handleDeleteMember = async () => {
    if (!company) {
      addErrorNotification({
        message: t("common:error.unknown"),
        position: "tc",
      });

      return;
    }
    try {
      const res = await deleteMemberReq(
        company.id,
        selectedMember?.id as string,
        {
          pending: !!selectedMember?.pending,
        },
      );

      if (res.isSuccess) {
        addSuccessNotification({
          message: t("settings:member.remove.success", {
            email: selectedMember?.email,
          }),
          position: "tc",
        });
        setMembers(res.members);
      } else {
        addErrorNotification({
          message: t("settings:member.delete.error"),
          position: "tc",
        });
      }
      clearSelectedMember();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      logger.error(`[ManagersList][handleDeleteMember] ${errorMessage}`);

      addErrorNotification({
        message: t("settings:member.delete.error"),
        position: "tc",
      });
    }
  };

  const showTickMark = (member: IMember | null, role: string) => (
    <div className="tick">{member?.role === role && <Check />}</div>
  );

  const renderEditOption = () => {
    const onKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>,
      callback: () => void,
    ) => {
      if (event.code === "Enter") {
        callback();
      }
    };

    const selectManager = () => handleMemberUpdate("manager");
    const selectAssistant = () => handleMemberUpdate("assistant");

    return (
      <Popover
        open={!!(isEdit && showEditOption && selectedMember)}
        anchorEl={anchorElm}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{
          minWidth: "270px",
          minHeight: "210px",
        }}
        onClose={clearSelectedMember}
        data-testid="context-menu-popover"
      >
        <div className="roleedit">
          <div className="option">
            <div
              data-testid="event-manager"
              className="info"
              onClick={selectManager}
              onKeyDown={(event) => onKeyDown(event, selectManager)}
              role="button"
              tabIndex={0}
            >
              <Typography className="title">
                {t("settings:event.manager")}
              </Typography>
              <Typography className="desc">
                {t("settings:event.manager.desc")}
              </Typography>
            </div>
            {showTickMark(selectedMember, "manager")}
          </div>

          <div
            data-testid="event-assistant"
            className="option"
            onClick={selectAssistant}
            onKeyDown={(event) => onKeyDown(event, selectAssistant)}
            role="button"
            tabIndex={0}
          >
            <div className="info">
              <Typography className="title">
                {t("settings:event.assistant")}
              </Typography>
              <Typography className="desc">
                {t("settings:event.assistant.desc")}
              </Typography>
            </div>
            {showTickMark(selectedMember, "assistant")}
          </div>

          <div
            className="option"
            onClick={handleRemove}
            data-testid="click-here"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => onKeyDown(event, handleRemove)}
          >
            <Typography className="remove">
              {t("settings:remove.account")}
            </Typography>
            <Delete className="icon" />
          </div>
        </div>
      </Popover>
    );
  };

  const checkAndUpdateEventAccess = () => {
    setShowEventList(false);

    if (!memberEditInfo?.member) {
      return;
    }
    const { member, events } = memberEditInfo;
    const mappedEvents = member.events || [];

    if (mappedEvents.sort().join(",") !== events?.sort().join(",")) {
      updateAssistantAccess(member, events);
    }
  };

  const renderEventsList = () => (
    <Popover
      open={!!(showEventList && memberEditInfo?.member)}
      onClose={checkAndUpdateEventAccess}
      anchorEl={anchorElm}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <CheckboxList
        minWidth
        allEvents={allEvents}
        mappedEvents={memberEditInfo?.events || []}
        onChange={(updatedEvents) =>
          setMemberEditInfo({
            ...memberEditInfo,
            events: updatedEvents,
          })
        }
      />
    </Popover>
  );

  const renderConfirmDialog = () => {
    const { member, operation, role } = memberEditInfo || {};

    return (
      <Dialog
        open={showConfirmDialogue}
        onClose={clearSelectedMember}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="xs"
        style={{
          borderRadius: "10px",
        }}
        data-testid="confirm-dialog"
      >
        <DialogContent style={{ padding: "30px 40px 0px 30px" }}>
          <DialogContentText>
            <Typography style={{ wordWrap: "break-word" }}>
              {operation === MemberOperation.DELETE &&
                member &&
                t("settings:member.remove.confirm", {
                  email: member?.email,
                })}
              {operation === MemberOperation.UPDATE &&
                member &&
                role &&
                t("settings:member.role.confirm", {
                  email: member?.email,
                  role: ROLE_INFO[role as string],
                })}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "0px 40px 20px 0px" }}>
          <Button
            variant="secondary"
            color="dark"
            size="sm"
            onClick={clearSelectedMember}
            style={{ minWidth: "90px" }}
          >
            {t("common:no")}
          </Button>
          <Button
            variant="primary"
            color="blue"
            size="sm"
            onClick={
              operation === MemberOperation.DELETE
                ? handleDeleteMember
                : handleUpdateMember
            }
            style={{ minWidth: "100px" }}
          >
            {t("common:yes")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleCancelAdd = () => {
    setIsAddOpen(false);
  };

  const getAllowedMembers = useCallback(() => {
    if (maxAllowedManagers < 0) {
      return t("common:unlimited");
    }

    return maxAllowedManagers + maxAllowedAssistants;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxAllowedManagers]);

  const memberCountText = useCallback(() => {
    const teamMembersInfo = t("settings:members.limit.info", {
      maxAllowedMembers: getAllowedMembers(),
    });

    if (maxAllowedAssistants !== maxAllowedManagers) {
      const assistantInfo = t("settings:members.legacy.limit.info", {
        maxAllowedAssistants,
      });

      return `${teamMembersInfo} ${assistantInfo}`;
    }

    return teamMembersInfo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxAllowedManagers, maxAllowedAssistants]);

  if (!company) {
    return null;
  }

  return (
    <>
      <Typography variant="h3">{t("settings:team.members")}</Typography>

      {isMobileView ? (
        <div className="mobile-view">
          <Typography>{t("settings:team.members.mobile.warn")}</Typography>
        </div>
      ) : (
        <div className="managers-list">
          <div className="info">
            <Box>
              <Typography className="usage">
                {`${t("settings:team.members")}: ${
                  members.length
                }/${getAllowedMembers()}`}
                <IconButtonWithTooltip
                  id="members-slot-help"
                  color="primary"
                  title={memberCountText()}
                  className="icon"
                >
                  <Info fontSize="small" color="primary" />
                </IconButtonWithTooltip>
              </Typography>
            </Box>
            {isEdit && (
              <div>
                <Button
                  variant="secondary"
                  color="blue"
                  size="md"
                  onClick={handleAddManager}
                  disabled={myTrialExpired}
                >
                  {t("settings:team.member.add")}
                </Button>
              </div>
            )}
          </div>
          <Paper className="paper-root">
            <div className="table-wrapper scroll">
              <Table className="managers-table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="40%" align="left">
                      {t("common:email")}
                    </TableCell>
                    <TableCell width="30%" align="left">
                      {t("settings:event.permission")}
                    </TableCell>
                    <TableCell width="30%" align="left">
                      {t("settings:event.access.level")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderRows()}</TableBody>
              </Table>
            </div>
          </Paper>
          {myTrialExpired && (
            <FormHelperText error>
              {t("company:trial.expired.message")}
            </FormHelperText>
          )}
          {showConfirmDialogue && renderConfirmDialog()}
          {showEditOption && renderEditOption()}
          {showEventList && renderEventsList()}
          {isAddOpen && (
            <AddCompanyMember
              company={company}
              allEvents={allEvents}
              handleClose={handleCancelAdd}
              updateMembers={(updatedMembers) => setMembers(updatedMembers)}
            />
          )}
        </div>
      )}
      <UpgradePlanPopup
        upgradeMessage={upgradeMessage}
        onClose={() => setUpgradeMessage("")}
      />
    </>
  );
};

export default ManagersList;
